import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Briefcase } from "lucide-react";

import AdvancedEditJobModal from "@/components/AdvancedEditJobModal";
import ChangeStatusModal from "@/components/ChangeStatusModal";
import ResendNotificationModal from "@/components/ResendNotificationModal";
import CancelHiredJobModal from "@/components/CancelHiredJobModal";
import CancelCrewMemberModal from "@/components/CancelCrewMemberModal";
import { getAdminEmail } from "@/utils/adminAuth";

import JobsHeader from "@/components/admin-jobs/JobsHeader";
import JobsToolbar from "@/components/admin-jobs/JobsToolbar";
import JobsTable from "@/components/admin-jobs/JobsTable";
import JobsMobileCards from "@/components/admin-jobs/JobsMobileCards";
import JobsPagination from "@/components/admin-jobs/JobsPagination";
import JobDetailsModal from "@/components/admin-jobs/JobDetailsModal";
import JobApprovalModal from "@/components/admin-jobs/JobApprovalModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AdminJobsRedesigned = () => {
  const navigate = useNavigate();

  // Core list state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(15);
  const [totalJobs, setTotalJobs] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [stats, setStats] = useState({ totalJobs: 0, pendingJobs: 0, activeJobs: 0, completedJobs: 0 });

  // Modal states
  const [editingJob, setEditingJob] = useState(null);
  const [changingStatusJob, setChangingStatusJob] = useState(null);
  const [resendingNotificationJob, setResendingNotificationJob] = useState(null);
  const [approvalJob, setApprovalJob] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState(0);
  const [loadingEligibleCount, setLoadingEligibleCount] = useState(false);

  // Details modal state
  const [viewingJob, setViewingJob] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [detailsTab, setDetailsTab] = useState("overview");
  const [sendingFollowup, setSendingFollowup] = useState(false);

  // Cancel crew member state
  const [cancellingHiredJob, setCancellingHiredJob] = useState(null);
  const [cancellingCrewMember, setCancellingCrewMember] = useState(null);
  const [cancellingCrewMemberIndex, setCancellingCrewMemberIndex] = useState(null);
  const [showCancelCrewMemberModal, setShowCancelCrewMemberModal] = useState(false);

  // Admin check
  useEffect(() => {
    if (!localStorage.getItem("shootzu_admin")) navigate("/szadmin");
  }, [navigate]);

  // Debounce search
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  // Fetch on page/search/filter change
  useEffect(() => { fetchJobs(); }, [currentPage, debouncedSearch, statusFilter]);
  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/admin/stats`);
      setStats({ totalJobs: r.data.total_jobs || 0, pendingJobs: r.data.pending_jobs || 0, activeJobs: r.data.active_jobs || 0, completedJobs: r.data.completed_jobs || 0 });
    } catch { /* ignore */ }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${BACKEND}/api/admin/all-jobs`, {
        params: { page: currentPage, limit: jobsPerPage, search: debouncedSearch || undefined, status: statusFilter !== "all" ? statusFilter : undefined },
      });
      setJobs(r.data.jobs || []);
      setTotalJobs(r.data.total || 0);
      setSelectedJobs(new Set());
    } catch {
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const fetchJobDetails = async (job) => {
    setViewingJob(job); setLoadingDetails(true); setJobDetails(null); setTransactionHistory([]); setDetailsTab("overview");
    try {
      const r = await axios.get(`${BACKEND}/api/admin/jobs/${job.id}/details`);
      setJobDetails(r.data);
      if (r.data.job?.stripe_payment_intent_id || r.data.job?.assigned_crew) fetchTransactionHistory(job.id);
    } catch {
      toast.error("Failed to load job details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchTransactionHistory = async (jobId) => {
    setLoadingTransactions(true);
    try {
      const r = await axios.get(`${BACKEND}/api/admin/jobs/${jobId}/transactions`);
      setTransactionHistory(r.data.transactions || []);
    } catch {
      setTransactionHistory([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Action handlers
  const handleDeleteJob = async (jobId, jobTitle) => {
    if (!window.confirm(`Delete "${jobTitle}"?\n\nThis will also delete all applications.`)) return;
    try {
      const r = await axios.delete(`${BACKEND}/api/admin/jobs/${jobId}`);
      if (r.data.success) { toast.success(`Deleted "${jobTitle}"`); fetchJobs(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete job"); }
  };

  const handleBulkDelete = async () => {
    if (selectedJobs.size === 0) return;
    const count = selectedJobs.size;
    if (!window.confirm(`Delete ${count} selected job${count > 1 ? "s" : ""}?\n\nThis cannot be undone.`)) return;
    setIsDeleting(true);
    let deleted = 0, failed = 0;
    for (const jobId of selectedJobs) {
      try { await axios.delete(`${BACKEND}/api/admin/jobs/${jobId}`); deleted++; } catch { failed++; }
    }
    setIsDeleting(false);
    if (deleted > 0) toast.success(`Deleted ${deleted} job${deleted > 1 ? "s" : ""}`);
    if (failed > 0) toast.error(`Failed to delete ${failed} job${failed > 1 ? "s" : ""}`);
    fetchJobs();
  };

  const toggleSelectJob = (jobId) => {
    const n = new Set(selectedJobs);
    n.has(jobId) ? n.delete(jobId) : n.add(jobId);
    setSelectedJobs(n);
  };

  const toggleSelectAll = () => {
    setSelectedJobs(selectedJobs.size === jobs.length ? new Set() : new Set(jobs.map((j) => j.id)));
  };

  const handleChangeStatus = async (newStatus, notes, sendNotifications = true) => {
    try {
      const r = await axios.put(`${BACKEND}/api/admin/jobs/${changingStatusJob.id}/status`, null, { params: { status: newStatus, notes: notes || undefined, send_notifications: sendNotifications } });
      if (r.data.success) { toast.success(r.data.message); setChangingStatusJob(null); fetchJobs(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change status"); }
  };

  const handleQuickApprove = async (job) => {
    setApprovalJob(job); setLoadingEligibleCount(true);
    try {
      const r = await axios.get(`${BACKEND}/api/admin/count-job-notification-recipients/${job.id}`);
      setEligibleUsers(r.data.count || 0);
    } catch { setEligibleUsers(0); }
    finally { setLoadingEligibleCount(false); setShowApprovalModal(true); }
  };

  const handleApprovalDecision = async (withNotifications) => {
    try {
      const r = await axios.post(`${BACKEND}/api/admin/jobs/${approvalJob.id}/approve`, { notify_users: withNotifications, admin_email: "hello@shootzu.com" });
      if (r.data.success) {
        toast.success(withNotifications ? `Approved! ${eligibleUsers} users notified.` : "Approved without notifications.");
        setShowApprovalModal(false); setApprovalJob(null); fetchJobs();
      }
    } catch { toast.error("Failed to approve job"); }
  };

  const handleSendFollowup = async () => {
    if (!viewingJob) return;
    try {
      setSendingFollowup(true);
      const token = localStorage.getItem("shootzu_admin_token");
      await axios.post(`${BACKEND}/api/admin/jobs/${viewingJob.id}/send-followup`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Followup email sent successfully");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send followup email"); }
    finally { setSendingFollowup(false); }
  };

  const totalPages = Math.ceil(totalJobs / jobsPerPage);

  // Shared action callbacks for table/cards
  const viewCallbacks = {
    onViewDetails: fetchJobDetails,
    onQuickApprove: handleQuickApprove,
    onEdit: (job) => setEditingJob(job),
    onChangeStatus: (job) => setChangingStatusJob(job),
    onResendNotification: (job) => setResendingNotificationJob(job),
    onDelete: handleDeleteJob,
  };

  const totalLabel = `${totalJobs.toLocaleString()} jobs${statusFilter !== "all" ? ` · ${statusFilter.replace(/_/g, " ")}` : ""}`;

  return (
    <div>
      {/* Sticky page header */}
      <header className="sz-header">
        <h1 className="sz-h1 m-0">Jobs</h1>
        <div className="text-[12.5px] text-[#8A90A2] mt-[3px]">{totalLabel}</div>
      </header>

      <main className="p-[22px_30px_56px] max-w-[1480px]">
        {/* Stat cards */}
        <JobsHeader stats={stats} loading={loading && jobs.length === 0} />

        {/* Sticky toolbar (sits under the sticky header) */}
        <div className="sticky z-10 mt-[18px]" style={{ top: 73 }}>
          <JobsToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={(s) => { setStatusFilter(s); setCurrentPage(1); }}
            selectedCount={selectedJobs.size}
            isDeleting={isDeleting}
            onBulkDelete={handleBulkDelete}
            onClearSelection={() => setSelectedJobs(new Set())}
            totalJobs={totalJobs}
            statusCounts={{ all: stats.totalJobs, PENDING_APPROVAL: stats.pendingJobs }}
          />
        </div>

        {/* Content */}
        {loading ? (
          <div className="mt-4 space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-[14px]" />)}</div>
        ) : jobs.length === 0 ? (
          <div className="sz-card mt-4 text-center py-16">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "#F1F3F7" }}>
              <Briefcase className="w-7 h-7 text-[#A8AEBC]" />
            </div>
            <p className="font-semibold text-[#0C1120] mb-1">No jobs found</p>
            <p className="text-sm text-[#8A90A2]">{searchQuery || statusFilter !== "all" ? "Try adjusting your search or filters" : "No jobs have been posted yet"}</p>
          </div>
        ) : (
          <>
            <JobsTable jobs={jobs} selectedJobs={selectedJobs} onToggleSelect={toggleSelectJob} onToggleSelectAll={toggleSelectAll} {...viewCallbacks} />
            <JobsMobileCards jobs={jobs} selectedJobs={selectedJobs} onToggleSelect={toggleSelectJob} {...viewCallbacks} />
            <JobsPagination currentPage={currentPage} totalPages={totalPages} totalJobs={totalJobs} jobsPerPage={jobsPerPage} onPageChange={setCurrentPage} />
          </>
        )}
      </main>

      {/* Modals */}
      {editingJob && (
        <AdvancedEditJobModal job={editingJob} isOpen={!!editingJob} onClose={() => setEditingJob(null)} onUpdate={() => { fetchJobs(); setEditingJob(null); }} />
      )}
      <ChangeStatusModal job={changingStatusJob} isOpen={!!changingStatusJob} onClose={() => setChangingStatusJob(null)} onStatusChange={handleChangeStatus} isDark={false} />
      <ResendNotificationModal job={resendingNotificationJob} isOpen={!!resendingNotificationJob} onClose={() => setResendingNotificationJob(null)} onSuccess={() => { toast.success("Notifications sent"); setResendingNotificationJob(null); }} />

      <JobApprovalModal
        isOpen={showApprovalModal}
        job={approvalJob}
        eligibleUsers={eligibleUsers}
        loadingCount={loadingEligibleCount}
        onApproveWithNotify={() => handleApprovalDecision(true)}
        onApproveWithoutNotify={() => handleApprovalDecision(false)}
        onClose={() => { setShowApprovalModal(false); setApprovalJob(null); }}
      />

      <JobDetailsModal
        viewingJob={viewingJob}
        jobDetails={jobDetails}
        loadingDetails={loadingDetails}
        transactionHistory={transactionHistory}
        loadingTransactions={loadingTransactions}
        detailsTab={detailsTab}
        onTabChange={setDetailsTab}
        onClose={() => { setViewingJob(null); setJobDetails(null); }}
        onEdit={(job) => { setEditingJob(job); setViewingJob(null); setJobDetails(null); }}
        onChangeStatus={(job) => { setChangingStatusJob(job); setViewingJob(null); setJobDetails(null); }}
        onAuditLog={(jobId) => { setViewingJob(null); setJobDetails(null); navigate(`/szadmin/offer-audit-log?job_id=${jobId}`); }}
        onCancelHiredJob={(job) => setCancellingHiredJob(job)}
        onCancelCrewMember={(crew, index) => { setCancellingCrewMember(crew); setCancellingCrewMemberIndex(index); setShowCancelCrewMemberModal(true); }}
        onSendFollowup={handleSendFollowup}
        sendingFollowup={sendingFollowup}
      />

      <CancelHiredJobModal
        job={cancellingHiredJob}
        jobDetails={jobDetails}
        isOpen={!!cancellingHiredJob}
        onClose={() => setCancellingHiredJob(null)}
        onSuccess={() => { setCancellingHiredJob(null); setViewingJob(null); setJobDetails(null); fetchJobs(); }}
      />

      <CancelCrewMemberModal
        isOpen={showCancelCrewMemberModal}
        job={viewingJob ? { id: viewingJob.id, title: viewingJob.title, jobDate: viewingJob.date } : null}
        crewMember={cancellingCrewMember}
        crewMemberIndex={cancellingCrewMemberIndex}
        userEmail={getAdminEmail()}
        onClose={() => { setShowCancelCrewMemberModal(false); setCancellingCrewMember(null); setCancellingCrewMemberIndex(null); }}
        onSuccess={() => { setShowCancelCrewMemberModal(false); setCancellingCrewMember(null); setCancellingCrewMemberIndex(null); if (viewingJob) fetchJobDetails(viewingJob); fetchJobs(); }}
        adminMode
      />
    </div>
  );
};

export default AdminJobsRedesigned;
